import { canUseDOM } from '@ps/web-core-utils';
import type { FirstWallet } from '@ps/web-martech-cms-types';
import { produce } from 'immer';
import { isEqual } from 'lodash';
import type { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, first, skip, skipUntil, take } from 'rxjs/operators';

import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import type { Slicer, WidgetEffectParams, WidgetModelConfig } from '@swf/core/store';
import type {
  EffectParams,
  ExtractDispatchFromModel,
  ExtractSelectFromModel
} from '@swf/core/store/types';
import { currencyChange, login, logout } from '@swf/core/utils/actions';
import { type Balance, CashierLimitTypes, type DepositLimit } from '@swf/first';

export interface CashierState {
  userLoggedOut?: boolean;
  needGoLobby?: boolean;
  balances?: Balance[];
  limits?: DepositLimit[];
  serviceLoading?: boolean;
}

const defaultState: CashierState = {
  userLoggedOut: false,
  needGoLobby: false,
  balances: [],
  limits: [],
  serviceLoading: true
};

const reducers = {
  clearNeedGoLobby: (state: CashierState): CashierState =>
    produce(state, (draft) => {
      draft.needGoLobby = defaultState.needGoLobby;
    }),
  set: (state: CashierState, payload: CashierState) => ({
    ...state,
    ...payload
  })
};

const effects = {
  async preload(_: undefined, params: WidgetEffectParams<FirstWallet>): Promise<void> {
    const { rootState, actions, select, widget, dispatch } = params;
    const clientAuthenticated = select.session.isClientAuthenticated(rootState);
    const widgetDispatch: ExtractDispatchFromModel<CashierWidgetModel> = dispatch[widget.uid];
    let dismissLoginSub: Subscription;
    const {
      fetch,
      fetchBalances,
      fetchLimits,
      set,
      clearNeedGoLobby
    }: ExtractDispatchFromModel<CashierWidgetModel> = widgetDispatch;
    const widgetSelect: ExtractSelectFromModel<CashierWidgetModel> = select[widget.uid];

    if (clientAuthenticated) {
      set({ userLoggedOut: false, serviceLoading: true });
      fetch();
    }

    login(actions)?.subscribe(async () => {
      dismissLoginSub?.unsubscribe();
      set({ userLoggedOut: false, serviceLoading: true });
      fetch();
    });

    logout(actions)?.subscribe(async () => {
      set({ userLoggedOut: true, needGoLobby: true, serviceLoading: true });
    });

    // trigger do-login
    if (canUseDOM && !clientAuthenticated) {
      actions
        .pipe(
          first(
            ({ state }) =>
              select.session.hasClientSession(state) &&
              !select.session.isAuthenticated(state) &&
              widgetSelect.get(state).userLoggedOut !== true
          )
        )
        .subscribe(() => dispatch.modal.enqueue({ path: '/auth/login' }));
    }

    if (canUseDOM) {
      // trigger go-lobby after dismissing login dialog
      if (!clientAuthenticated) {
        const loginDialogClosedWithoutAuth = (actions: EffectParams['actions']) =>
          actions.pipe(
            filter(
              ({ type, state }) =>
                'modal/close' === type && !select.session.isClientAuthenticated(state)
            )
          );
        const loginDialogOpened = (actions: EffectParams['actions']) =>
          actions.pipe(
            filter(({ state }) => select.modal.getCurrent(state)?.path === '/auth/login')
          );

        dismissLoginSub = actions
          .pipe(
            skipUntil(loginDialogOpened(actions) as typeof actions),
            skipUntil(loginDialogClosedWithoutAuth(actions) as typeof actions),
            take(1)
          )
          .subscribe(() => {
            set({ needGoLobby: true });
          });
      }

      // subscribe account balance changes and fetch Balances again
      actions
        .pipe(
          distinctUntilChanged((prev, curr) =>
            isEqual(select.account.getBalances(prev.state), select.account.getBalances(curr.state))
          ),
          skip(1)
        )
        .subscribe(async () => fetchBalances());

      // subscribe to self-imposed and regulatory limits changes and fetch the limits again
      actions
        .pipe(
          distinctUntilChanged(
            (prev, curr) =>
              select.account.getUserLimits(prev.state) === select.account.getUserLimits(curr.state)
          ),
          skip(1)
        )
        .subscribe(() => fetchLimits());
    }

    // subscribe account currency changes and fetch Balances again
    currencyChange(actions)?.subscribe();
    widget.unmount.subscribe(clearNeedGoLobby);
  },
  async fetch(_: undefined, params: WidgetEffectParams<FirstWallet>) {
    const {
      dispatch,
      select,
      rootState,
      widget: { uid }
    } = params;
    const isEmbedded = select.device.isEmbedded(rootState);
    const widgetDispatch: ExtractDispatchFromModel<CashierWidgetModel> = dispatch[uid];

    await Promise.allSettled([widgetDispatch.fetchBalances(), widgetDispatch.fetchLimits()]).then(
      () => {
        widgetDispatch.set({ serviceLoading: false });
        if (isEmbedded) {
          // on mobile devices the cashier path will not hide the/
          // native loading screen and will leave to us to do it
          window?.starsweb?.app?.setLoading?.(false);
        }
      }
    );
  },
  async fetchBalances(
    _: undefined,
    { request, dispatch, widget: { uid } }: WidgetEffectParams<FirstWallet>
  ) {
    const widgetDispatch: ExtractDispatchFromModel<CashierWidgetModel> = dispatch[uid];
    try {
      const { body: balances } = await request<Balance[]>({ service: 'wc3.wallet.get.balances' });
      widgetDispatch.set({ balances: balances.filter(({ created }) => created) });
    } catch (e) {
      widgetDispatch.set({ balances: [] });
    }
  },

  async fetchLimits(
    _: undefined,
    { request, dispatch, widget: { uid } }: WidgetEffectParams<FirstWallet>
  ) {
    const widgetDispatch: ExtractDispatchFromModel<CashierWidgetModel> = dispatch[uid];

    try {
      const { body: limits } = await request({
        service: 'wc3.limit.info'
      });

      widgetDispatch.set({ limits });
    } catch (e) {
      widgetDispatch.set({ limits: [] });
    }
  }
};

const selectors = (slice: Slicer<CashierState>) => ({
  get() {
    return slice;
  },
  getClosestLimit: () =>
    slice((state: CashierState) => {
      const selfImposedLimit = state.limits?.find(
        (limit) => limit?.limitType === CashierLimitTypes.SelfImposedLimit
      );
      const regulatoryLimit = state.limits?.find(
        (limit) => limit?.limitType === CashierLimitTypes.RegulatoryLimit
      );
      const overallClosest = state.limits && state?.limits[0];

      return {
        selfImposedLimit,
        regulatoryLimit,
        overallClosest
      };
    })
});

export interface CashierWidgetModel extends WidgetModelConfig<FirstWallet, CashierState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const cashierWidgetModel: CashierWidgetModel = {
  state: defaultState,
  reducers,
  effects,
  selectors
};

export const useCashierWidget: UseWidget<FirstWallet, CashierWidgetModel> = useWidget;
